import Colors from '../../utils/color';

/**
 * The corner FLAG badge — HITL-1655's three gestures, wherever they appear.
 *
 * ## One module because it is one mark
 *
 * A cart row, the co-pilot suggestion and the order status banner all offer the same
 * gesture: "file a report about THIS". Three components own those surfaces and none of
 * them knows about the other two, which is exactly how the same mark ends up drawn
 * three slightly different ways. It is declared once here instead.
 *
 * ## Why it is a corner badge and not an inline button
 *
 * It shipped inline first — a hover-revealed icon beside the cart item's name, a third
 * mark after Accept and Dismiss on the suggestion — and that put it INSIDE the row's
 * own content, competing with the controls that do the actual work. On the cart row it
 * sat a few pixels from Remove; on the suggestion it read as a third verdict alongside
 * the tick and the cross.
 *
 * The corner is structurally different: it is attached to the BOX rather than placed in
 * the row, so it says "about this whole thing" without taking a position in whatever
 * the box contains. That is the Issue button's badge convention already — `top: -6,
 * right: -6`, straddling the border — so this is the console quoting itself rather than
 * inventing a placement.
 *
 * ## The colour
 *
 * `Colors.amaranth` with a `Colors.mexicanRed` rim, both lifted verbatim from that
 * badge (`FlagIssueButton`). Amaranth is this console's ERROR tone — the Order Status
 * Banner's error ring — and the rim is the palette's dark red, there because a badge
 * that straddles a border dissolves into whichever side it overlaps without one.
 *
 * Deliberately NOT the other two reds: `Colors.requestStaff` is the intent bar's
 * escalation red and means "get a human to the lane", and `Colors.red` is pure
 * `#FF0000`, which is barely legible under white ink.
 *
 * Note this breaks the rule stated in `issueActionButtonStyle` — that amaranth is for
 * STATUS marks and pressable things wear the action palette. The rule loses here on
 * purpose: the agent's reference point for "flag" on this console is the Issue
 * button's red corner badge, and matching the thing it is named after is worth more
 * than an internal consistency nobody reads the codebase to check.
 *
 * ## Revealed on hover, on all three
 *
 * The badge it quotes is always painted, and this one is not. The difference is that
 * the Issue button's badge is a COUNT — it appears because there is something to
 * report, and an empty one is never drawn — whereas these three are offers, present on
 * every row of every cart whether or not anything is wrong. Six items would mean six
 * red marks standing over an order that is fine, which trains an agent to stop seeing
 * the colour exactly where it is supposed to mean something.
 *
 * `:focus-within` is not garnish here. Agent stations are mouse AND keyboard, and a
 * hover-only control is simply absent to anyone working the keyboard — so the badge
 * has to appear when it is tabbed to as well.
 *
 * `visibility` + `opacity`, never `display: none`: the badge is absolutely positioned
 * so it costs no layout either way, but the pair is what lets the fade run instead of
 * having it snap in under the cursor.
 */

/** The hook the reveal rule below matches on. */
export const ISSUE_FLAG_BADGE_CLASS = 'issue-flag-badge';

/**
 * Goes on the CONTAINER the badge hangs off — the cart card, the suggestion box, the
 * status bar — not on the badge itself. A rule that has to know about its own parent's
 * hover state cannot be written from inside the child.
 */
export const ISSUE_FLAG_BADGE_REVEAL_SX = {
  [`& .${ISSUE_FLAG_BADGE_CLASS}`]: {
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 120ms ease-in-out',
  },
  [`&:hover .${ISSUE_FLAG_BADGE_CLASS}, &:focus-within .${ISSUE_FLAG_BADGE_CLASS}`]:
    {
      visibility: 'visible',
      opacity: 1,
    },
} as const;
export const ISSUE_FLAG_BADGE_PX = 18;

export const ISSUE_FLAG_BADGE_SX = {
  position: 'absolute',
  // The Issue button badge's exact offsets. Roughly a third of the badge hangs
  // outside the border, which is what makes it read as attached TO the box rather
  // than as something sitting inside it.
  top: -6,
  right: -6,
  // Above the box's own content, below the flag panel — which is at 9999 and must
  // cover every one of these when it opens (`issueReportLayers`).
  zIndex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // `IconButton` brings its own 8px; unset it or the painted circle lands at 34px.
  padding: 0,
  minWidth: ISSUE_FLAG_BADGE_PX,
  width: ISSUE_FLAG_BADGE_PX,
  height: ISSUE_FLAG_BADGE_PX,
  borderRadius: '50%',
  backgroundColor: Colors.amaranth,
  color: Colors.white,
  boxShadow: `0 0 0 2px ${Colors.mexicanRed}`,
  // Held flat on hover, matching the badge this quotes — it holds one solid colour.
  // MUI's default hover is a translucent overlay of `color`, which on a filled
  // circle just washes it out.
  '&:hover': {
    backgroundColor: Colors.amaranth,
  },
  '&:focus-visible': {
    outline: `2px solid ${Colors.mexicanRed}`,
    outlineOffset: '1px',
  },
} as const;

/**
 * 11px inside an 18px circle, leaving ~3.5px of fill around the glyph — the same
 * proportion the suggestion box's 10px-in-16px badge uses.
 *
 * An INLINE style rather than `sx`, for the reason recorded in `PulseAdvise`:
 * `fontSize="small"` puts MUI's own 1.25rem class on the icon, and beating it from
 * the button needs a descendant selector whose win depends on emotion's injection
 * order.
 */
export const ISSUE_FLAG_BADGE_ICON_STYLE = { fontSize: '11px' } as const;
